import type { NurseWithRelation } from "@/types/nurse-type";
import type { NurseResponse } from "@/types/nurse-type";
import { NurseTableRow } from "./NurseTableRow";
import type { GroupMemberInMonth } from "@/api/nurses";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCallback, useMemo, useRef } from "react";
import { headerList } from "./rosterTable.config";
import { cn } from "@/utils/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrowUpDown, HelpCircle } from "lucide-react";
import { MonthlyMembershipProvider } from "./MonthlyMembershipContext";
import NightBulkHeaderControl from "./NightBulkHeaderControl";
import type { GradeConfigQueryResult } from "@/hooks/useGradeConfig";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import { OPTIONS as SHIFT_OPTIONS } from "@constants/SHIFT";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import { nurseGroupSortKey } from "@/utils/common-util";
import { useRowSwapFlipAnimation } from "../hooks/useRowSwapFlipAnimation";
import {
  isDispatchedDisplayRow,
  splitDispatchedRows,
} from "@/components/feature/nurse-membership/splitDispatchedRows";

type ShiftOption = { label: string; value: string; color?: string };
type MoveNurseHandler = (
  nurseId: string,
  direction: "up" | "down",
  listKind: "active" | "inactive",
  visibleNurseIds: readonly string[]
) => void;

const DEFAULT_SHIFT_COLORS = {
  D: "#4ECDC4",
  E: "#64B5F6",
  N: "#B8A5F5",
  M: "#F2B705",
  O: "#F27AB6",
} as const;

function normalizeShiftColor(color: string | null | undefined): string | null {
  if (!color) return null;
  const trimmed = color.trim();
  if (trimmed === "") return null;
  if (trimmed.startsWith("#") || trimmed.startsWith("rgb")) return trimmed;
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  if (/^\d+\s*,\s*\d+\s*,\s*\d+$/.test(trimmed)) {
    return `rgb(${trimmed})`;
  }
  return trimmed;
}

function defaultShiftColor(code: string): string | null {
  return DEFAULT_SHIFT_COLORS[code as keyof typeof DEFAULT_SHIFT_COLORS] ?? null;
}

export function ManagerTable({
  nurseDataWithRelation,
  selectedGroupId,
  activeNurseIdForSideProfile,
  selectIdForSideprofile,
  isPending,
  sortConfig,
  onSort,
  disableRowEdit = false,
  isAddNewNurse = false,
  setIsAddNewNurse,
  hideEditColumn = false,
  customHeaderList,
  addNurseModal = false,
  teamName,
  enableSelection = false,
  selectedNurses,
  onToggleSelection,
  className,
  memberStatusMap,
  year,
  month,
  enableNightBulkEdit = false,
  nurseCacheData,
  gradeData,
  shiftsData,
  isUseMid = false,
  groupName,
  onMoveNurse,
  showReorder = false,
  isActiveList = true,
}: {
  teamName: string;
  nurseDataWithRelation: NurseWithRelation[];
  selectedGroupId: string | null;
  // 근무자관리 월 셀렉터 기준 연/월 (나이트 개수 인라인 편집용)
  year?: number;
  month?: number;
  activeNurseIdForSideProfile: string | null;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  isPending?: boolean;
  sortConfig?: {
    key: "experience" | "grade" | "name" | null;
    order: "asc" | "desc" | null;
  };
  onSort?: (key: "experience" | "grade" | "name") => void;
  disableRowEdit?: boolean;
  isAddNewNurse?: boolean;
  setIsAddNewNurse?: React.Dispatch<React.SetStateAction<boolean>>;
  hideEditColumn?: boolean;
  customHeaderList?: typeof headerList;
  addNurseModal?: boolean;
  enableSelection?: boolean;
  selectedNurses?: Set<string>;
  onToggleSelection?: (nurseId: string) => void;
  className?: string;
  /** [Phase4 B-1] nurse_id→선택 월 소속 상태. 없으면 배지 미표시(기존 동일). */
  memberStatusMap?: Map<string, GroupMemberInMonth>;
  /** "나이트 개수" 헤더에 일괄 설정(연필) 노출. 활성 표에서만 true. */
  enableNightBulkEdit?: boolean;
  nurseCacheData?: NurseResponse[];
  gradeData?: GradeConfigQueryResult;
  shiftsData?: ShiftReturnType;
  isUseMid?: boolean;
  groupName?: string;
  onMoveNurse?: MoveNurseHandler;
  showReorder?: boolean;
  isActiveList?: boolean;
}) {
  const filteredHeaderList = useMemo(() => {
    let headers = customHeaderList || headerList;

    if (!customHeaderList && hideEditColumn) {
      headers = headers.filter((header) => header.label !== "관리");
    }

    if (!enableSelection) {
      headers = headers.filter(
        (h) => h.label.trim().toLowerCase() !== "checkbox"
      );
    }

    return headers;
  }, [hideEditColumn, customHeaderList, enableSelection]);

  // 야간 일괄 적용 대상 그룹: admin 은 selectedGroupId, 비-admin(HN)은 prop 이 null 이라
  // 표시중인 근무자의 group_id 로 폴백(= 현재 병동).
  const nightBulkGroupId =
    selectedGroupId ||
    nurseDataWithRelation.find((n) => n.group_id)?.group_id ||
    "";

  const newNurseTemplate: NurseWithRelation = useMemo(
    () => ({
      nurse_id: `temp_new_${Date.now()}`,
      account_id: "",
      name: "",
      emp_num: 0,
      gender: "M",
      age: null,
      role: "RN",
      grade: 0,
      experience: 1,
      is_head_nurse: false,
      weekly_off_weekday: null,
      allowed_shifts: [],
      preceptor_id: null,
      joining_date: null,
      resignation_date: null,
      nurse_memo: "",
      active: 1,
      team_id: null,
      sequence: 0,
      is_weekend_off: false,
      fixed_shift: null,
      preceptor: null,
      preceptees: [],
      group_id: selectedGroupId || "",
      level_: "일반",
      office_id: "",
      personal_off_adjustment: 0,
      enable_aide: false,
      enable_nurse_pair_preference: false,
      wanted_max_requests: null,
      resignation_reason: null,
      resignation_reason_memo: null,
    }),
    [selectedGroupId]
  );

  const displayNurses = useMemo(() => {
    const sortedRows = splitDispatchedRows(nurseDataWithRelation).displayRows;
    if (isAddNewNurse && !disableRowEdit) {
      return [newNurseTemplate, ...sortedRows];
    }
    return sortedRows;
  }, [isAddNewNurse, disableRowEdit, newNurseTemplate, nurseDataWithRelation]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const visibleNurseIds = useMemo(
    () => displayNurses.map((nurse) => nurse.nurse_id),
    [displayNurses]
  );
  const captureRowPositions = useRowSwapFlipAnimation(
    tableContainerRef,
    visibleNurseIds
  );

  const shiftOptions = useMemo<ShiftOption[]>(() => {
    if (!shiftsData || shiftsData.length === 0) {
      const base = SHIFT_OPTIONS.slice(1).map((o) => ({
        label: (o as { short: string }).short,
        value: (o as { short: string }).short,
        color: defaultShiftColor((o as { short: string }).short) ?? undefined,
      }));
      return isUseMid !== false
        ? [
            ...base,
            { label: "M", value: "M", color: defaultShiftColor("M") ?? undefined },
          ]
        : base;
    }

    const deduped = new Map<string, ShiftOption>();
    getDefaultShiftsSortedBySequence(shiftsData, { excludeOff: true }).forEach(
      (shift) => {
        const code = shift.default_shift;
        if (!code) return;
        if (isUseMid === false && code === "M") return;
        if (!deduped.has(code)) {
          const color =
            normalizeShiftColor(shift.color) ?? defaultShiftColor(code) ?? undefined;
          deduped.set(code, {
            label: shift.shift_id || code,
            value: code,
            color,
          });
        }
      }
    );

    return Array.from(deduped.values());
  }, [shiftsData, isUseMid]);

  const fixedShiftOptions = useMemo<ShiftOption[]>(() => {
    if (!shiftsData) return [];
    const list = shiftsData
      .filter((shift) => shift.shift_gb === "고정")
      .map((shift) => ({
        label: shift.name,
        value: shift.shift_id,
        color: normalizeShiftColor(shift.color) ?? undefined,
      }));
    if (isUseMid === false) return list.filter((o) => o.value !== "M");
    return list;
  }, [shiftsData, isUseMid]);

  const shiftColorMap = useMemo(() => {
    const map = new Map<string, string>();
    (shiftsData ?? []).forEach((shift) => {
      const color =
        normalizeShiftColor(shift.color) ??
        defaultShiftColor(shift.default_shift) ??
        null;
      if (!color) return;
      if (shift.shift_id && !map.has(shift.shift_id)) {
        map.set(shift.shift_id, color);
      }
      if (shift.default_shift && !map.has(shift.default_shift)) {
        map.set(shift.default_shift, color);
      }
    });
    Object.entries(DEFAULT_SHIFT_COLORS).forEach(([code, color]) => {
      if (!map.has(code)) map.set(code, color);
    });
    return map;
  }, [shiftsData]);

  const visibleColumnLabels = useMemo(() => {
    return filteredHeaderList
      .slice(1)
      .map((h) => h.label)
      .filter((label) => label !== "");
  }, [filteredHeaderList]);

  const handleCancelNewNurse = useCallback(() => {
    setIsAddNewNurse?.(false);
  }, [setIsAddNewNurse]);

  return (
    <section
      ref={tableContainerRef}
      className={cn(
        "w-full min-w-0 overflow-visible rounded-b-md border bg-white shadow-lg",
        className
      )}
      aria-label={teamName}
    >
      <Table
        className="w-full min-w-[1200px]"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          {filteredHeaderList.slice(1).map((header) => {
            return (
              <col
                key={header.label}
                style={header.label === "메모" ? undefined : { width: header.width }}
              />
            );
          })}
        </colgroup>
        <TableHeader
          className={cn(
            "bg-[#F0F0F2]",
            (enableSelection || addNurseModal) && "sticky top-0 z-8"
          )}
        >
          <TableRow>
            {filteredHeaderList.slice(1).map((header) => {
              if (header.label.trim().toLowerCase() === "checkbox") {
                return <TableHead key={header.label} className="text-center" />;
              }

              const isSortable =
                header.label === "경력" ||
                header.label === "Grade" ||
                header.label === "이름";
              const sortKey =
                header.label === "경력"
                  ? "experience"
                  : header.label === "Grade"
                    ? "grade"
                    : header.label === "이름"
                      ? "name"
                      : null;
              const isActive = sortKey !== null && sortConfig?.key === sortKey;
              const isWeeklyOff = header.label === "주휴";
              const isNightCount = header.label === "나이트 개수";
              const showNightBulk =
                isNightCount &&
                enableNightBulkEdit &&
                year != null &&
                month != null &&
                !!nightBulkGroupId;

              return (
                <TableHead
                  key={header.label}
                  className={cn(
                    "text-center",
                    isSortable &&
                      "cursor-pointer select-none hover:bg-gray-200",
                    isNightCount && "group"
                  )}
                  onClick={() => isSortable && sortKey && onSort?.(sortKey)}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span
                      className={cn(
                        isSortable && "underline",
                        isActive && "!text-sig !underline font-bold",
                        addNurseModal &&
                          "!text-text-main2 !text-[14px] !font-bold !no-underline font-pretendard"
                      )}
                    >
                      {header.label}
                    </span>
                    {isSortable && (
                      <span
                        className={cn(
                          "inline-flex items-center text-zinc-400",
                          isActive && "text-sig"
                        )}
                        aria-hidden="true"
                      >
                        {isActive ? (
                          <span className="text-sm">
                            {sortConfig?.order === "asc" ? "↑" : "↓"}
                          </span>
                        ) : (
                          <ArrowUpDown className="size-3.5" />
                        )}
                      </span>
                    )}

                    {isWeeklyOff && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              설정된 순환주기에 따라 주휴 변동일이 적용됩니다.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {showNightBulk && (
                      <NightBulkHeaderControl
                        groupId={nightBulkGroupId}
                        year={year as number}
                        month={month as number}
                      />
                    )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        {isPending ? (
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={filteredHeaderList.length - 1}
                className="text-center py-10"
              >
                <div className="flex justify-center items-center gap-2">
                  <span className="text-zinc-500">
                    팀 정보를 동기화 중입니다...
                  </span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            <MonthlyMembershipProvider
              memberStatusMap={memberStatusMap}
              selectedGroupId={selectedGroupId}
            >
              {displayNurses.map((nurse, idx) => {
                const previous = displayNurses[idx - 1];
                const next = displayNurses[idx + 1];
                const dispatchGroupTopBorder =
                  isDispatchedDisplayRow(nurse) &&
                  (!previous || !isDispatchedDisplayRow(previous));
                const canShowReorder =
                  showReorder &&
                  !disableRowEdit &&
                  !addNurseModal &&
                  !enableSelection &&
                  !nurse.nurse_id.startsWith("temp_") &&
                  !isDispatchedDisplayRow(nurse);
                const canMoveUp =
                  canShowReorder &&
                  !!previous &&
                  !previous.nurse_id.startsWith("temp_") &&
                  !isDispatchedDisplayRow(previous) &&
                  nurseGroupSortKey(previous) === nurseGroupSortKey(nurse);
                const canMoveDown =
                  canShowReorder &&
                  !!next &&
                  !next.nurse_id.startsWith("temp_") &&
                  !isDispatchedDisplayRow(next) &&
                  nurseGroupSortKey(next) === nurseGroupSortKey(nurse);
                const moveListKind = isActiveList ? "active" : "inactive";

                return (
                  <NurseTableRow
                    key={nurse.nurse_id}
                    nurse={nurse}
                    idx={idx}
                    selectedGroupId={selectedGroupId}
                    activeForSideProfile={
                      nurse.nurse_id === activeNurseIdForSideProfile
                    }
                    selectIdForSideprofile={selectIdForSideprofile}
                    enableDragHandle={false}
                    disableEdit={disableRowEdit}
                    onCancelNewNurse={handleCancelNewNurse}
                    hideEditColumn={hideEditColumn}
                    visibleColumns={visibleColumnLabels}
                    enableSelection={enableSelection}
                    isSelected={selectedNurses?.has(nurse.nurse_id)}
                    onToggleSelection={onToggleSelection}
                    addNurseModal={addNurseModal}
                    year={year}
                    month={month}
                    nurseCacheData={nurseCacheData}
                    gradeData={gradeData}
                    shiftOptions={shiftOptions}
                    fixedShiftOptions={fixedShiftOptions}
                    shiftColorMap={shiftColorMap}
                    isUseMid={isUseMid}
                    groupName={groupName}
                    showReorder={canShowReorder}
                    canMoveUp={canMoveUp}
                    canMoveDown={canMoveDown}
                    dispatchGroupTopBorder={dispatchGroupTopBorder}
                    onMoveUp={() => {
                      captureRowPositions();
                      onMoveNurse?.(
                        nurse.nurse_id,
                        "up",
                        moveListKind,
                        visibleNurseIds
                      );
                    }}
                    onMoveDown={() => {
                      captureRowPositions();
                      onMoveNurse?.(
                        nurse.nurse_id,
                        "down",
                        moveListKind,
                        visibleNurseIds
                      );
                    }}
                  />
                );
              })}
            </MonthlyMembershipProvider>
            {displayNurses.length === 0 && (
              <TableRow className="bg-white!">
                <TableCell
                  colSpan={filteredHeaderList.length - 1}
                  className="text-center py-5"
                >
                  {addNurseModal ? (
                    <div className="flex items-center justify-center h-[200px]">
                      <span className="font-pretendard text-[14px] text-text-main2">
                        추가할 근무자를 선택하고
                        <span className="font-pretendard text-sig font-bold">
                          {" "}
                          [추가하기]
                        </span>
                        버튼을 눌러 근무자를 추가해주세요.
                      </span>
                    </div>
                  ) : (
                    "등록된 인원이 없습니다."
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    </section>
  );
}
